import { Stack, UnstyledButton, Group, Text, Divider, Box, Badge } from '@mantine/core';
import { useLocation, Link } from 'react-router-dom';
import { navigationItems } from '../../utils/navigationConfig';
import { usePlanningLimits } from '../../hooks/usePlanningLimits';

type MobileNavProps = {
  onClose: () => void;
};

export const MobileNav = ({ onClose }: MobileNavProps) => {
  const location = useLocation();
  const { isStartPlanningDisabled } = usePlanningLimits();

  // Group navigation items for better mobile organization
  const planningItems = navigationItems.slice(0, 3); // Dashboard, Plan Meals, Meal History
  const kitchenItems = navigationItems.slice(3); // Cookbook, Pantry

  const renderNavSection = (items: typeof navigationItems, title?: string) => (
    <>
      {title && (
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="md" pt="md">
          {title}
        </Text>
      )}
      <Stack gap="xs" p="md">
        {items.map((item) => {
          const isActive = location.pathname === item.href || 
            (item.href !== '/' && location.pathname.startsWith(item.href));
          
          // Check if this is the Plan Meals item and if planning is disabled
          const isPlanMealsItem = item.href === '/planning';
          const isDisabled = isPlanMealsItem && isStartPlanningDisabled;
          
          const button = isDisabled ? (
            <UnstyledButton
              key={item.href}
              onClick={(e) => e.preventDefault()}
              style={{
                padding: '12px 16px',
                borderRadius: 'var(--mantine-radius-md)',
                backgroundColor: 'transparent',
                border: '1px solid transparent',
                transition: 'all 0.2s ease',
                textDecoration: 'none',
                cursor: 'not-allowed',
                opacity: 0.6
              }}
              styles={{
                root: {
                  '&:hover': {
                    backgroundColor: 'transparent',
                  }
                }
              }}
            >
              <Group gap="md" align="center">
                <Box
                  style={{
                    color: 'var(--mantine-color-gray-4)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {item.icon}
                </Box>
                <Stack gap={2} style={{ flex: 1 }}>
                  <Group gap="xs" align="center">
                    <Text 
                      size="sm" 
                      fw={500}
                      c="gray.4"
                    >
                      {item.label}
                    </Text>
                    <Badge 
                      size="xs" 
                      variant="light" 
                      color="orange"
                      style={{ fontSize: '10px' }}
                    >
                      Limit reached
                    </Badge>
                  </Group>
                  <Text 
                    size="xs" 
                    c="gray.4"
                  >
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            </UnstyledButton>
          ) : (
            <UnstyledButton
              key={item.href}
              component={Link}
              to={item.href}
              onClick={onClose}
              style={{
                padding: '12px 16px',
                borderRadius: 'var(--mantine-radius-md)',
                backgroundColor: isActive ? 'var(--mantine-color-orange-0)' : 'transparent',
                border: isActive ? '1px solid var(--mantine-color-orange-2)' : '1px solid transparent',
                transition: 'all 0.2s ease',
                textDecoration: 'none'
              }}
              styles={{
                root: {
                  '&:hover': {
                    backgroundColor: isActive 
                      ? 'var(--mantine-color-orange-1)' 
                      : 'var(--mantine-color-gray-0)',
                  }
                }
              }}
            >
              <Group gap="md" align="center">
                <Box
                  style={{
                    color: isActive ? 'var(--mantine-color-orange-6)' : 'var(--mantine-color-gray-6)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {item.icon}
                </Box>
                <Stack gap={2} style={{ flex: 1 }}>
                  <Group gap="xs" align="center">
                    <Text
                      size="sm"
                      fw={isActive ? 600 : 500}
                      c={isActive ? 'orange.7' : 'dark'}
                    >
                      {item.label}
                    </Text>
                    {item.badge && (
                      <Badge
                        size="xs"
                        variant="light"
                        color={isActive ? "orange" : "violet"}
                        style={{ fontSize: '10px' }}
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </Group>
                  <Text
                    size="xs"
                    c="dimmed"
                  >
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            </UnstyledButton>
          );

          return button;
        })}
      </Stack>
    </>
  );

  return (
    <Stack gap={0}>
      {renderNavSection(planningItems, 'Planning')}
      <Divider />
      {renderNavSection(kitchenItems, 'Kitchen')}
    </Stack>
  );
};